import type { HelpTopicPair } from "../types";

const topic: HelpTopicPair = {
  th: {
    id: "getting-started",
    title: "เริ่มต้นใช้งานคอนโซล",
    summary:
      "คอนโซลผู้ดูแลระบบคือที่สำหรับจัดการบัญชีผู้ใช้ทั้งหมด กลุ่ม บทบาท วิธีเข้าสู่ระบบ และแอปพลิเคชันที่เชื่อมต่อกับระบบเข้าสู่ระบบกลาง (Single Sign-On) หน้านี้อธิบายวิธีเข้าสู่ระบบ ส่วนประกอบของหน้าจอ และเรื่องสิทธิ์ที่ควรรู้ก่อนเริ่มทำงาน",
    access:
      "ทุกคนที่มีบทบาทคอนโซล (เช่น owner, admin, auditor หรือบทบาทที่องค์กรสร้างเอง) และเป็นผู้ใช้ประเภท employee เปิดคอนโซลได้ เมนูและปุ่มที่เห็นจะขึ้นอยู่กับสิทธิ์ของบทบาทที่ได้รับ",
    shot: {
      file: "getting-started-layout.png",
      caption: "หน้าตาคอนโซล: แถบเมนูด้านซ้าย แถบหัวสีน้ำเงินด้านบน และเนื้อหาของหน้าที่เลือก",
    },
    sections: [
      {
        id: "sign-in",
        heading: "การเข้าสู่ระบบคอนโซล",
        body: [
          'เมื่อเปิดคอนโซลครั้งแรก (หรือเมื่อเซสชันหมดอายุ) จะเห็นหน้า "เข้าสู่ระบบคอนโซล" คอนโซลไม่มีช่องกรอกรหัสผ่านของตัวเอง การเข้าสู่ระบบทำที่หน้าเข้าสู่ระบบกลางของ auth server ซึ่งเป็นหน้าเดียวกับที่พนักงานใช้เข้าแอปอื่น ๆ ขององค์กร',
          "ที่หน้าเข้าสู่ระบบกลาง คุณพิมพ์อีเมล เบอร์โทรศัพท์ รหัสผู้ใช้งาน หรือชื่อผู้ใช้ อย่างใดอย่างหนึ่งที่ผูกกับบัญชีและยืนยันแล้ว แล้วตามด้วยรหัสผ่าน (หรือใช้ Google / Microsoft ถ้าองค์กรเปิดไว้) จากนั้นระบบจะขอการยืนยันตัวตนสองขั้นตอน (2FA) และพากลับมาที่คอนโซลหน้าเดิมที่คุณเปิดค้างไว้",
          'ด้านล่างปุ่มมีตัวเลือกภาษา "ไทย" / "English" สำหรับเปลี่ยนภาษาของหน้าจอได้ตั้งแต่ก่อนเข้าสู่ระบบ',
        ],
        shot: {
          file: "getting-started-login.png",
          caption: 'หน้า "เข้าสู่ระบบคอนโซล" กดปุ่ม "เข้าสู่ระบบด้วย …" เพื่อไปยังหน้าเข้าสู่ระบบกลาง',
        },
        steps: [
          "เปิดที่อยู่ของคอนโซลที่ได้รับจากผู้ดูแลระบบ",
          'กดปุ่ม "เข้าสู่ระบบด้วย Auth Platform" (ชื่ออาจต่างไปตามชื่อผลิตภัณฑ์ที่องค์กรตั้งไว้) ปุ่มจะเปลี่ยนเป็น "กำลังไปยังหน้าเข้าสู่ระบบ…"',
          "กรอกอีเมล เบอร์โทรศัพท์ รหัสผู้ใช้งาน หรือชื่อผู้ใช้ แล้วกรอกรหัสผ่าน",
          "ยืนยันตัวตนขั้นที่สองด้วยรหัสจากแอปยืนยันตัวตน (เช่น Google Authenticator) รหัสทางอีเมล หรือรหัสสำรอง ถ้ายังไม่เคยตั้งค่า ระบบจะพาไปหน้าตั้งค่า 2FA ก่อน",
          "ระบบพากลับมาที่คอนโซลโดยอัตโนมัติ",
        ],
        tips: [
          'ถ้าเห็นข้อความ "เซสชันหมดอายุ · เข้าสู่ระบบอีกครั้งแล้วจะกลับไปหน้าเดิม" แปลว่าคุณไม่ได้ใช้งานนานเกินไป กดปุ่มเข้าสู่ระบบอีกครั้งได้เลย ไม่มีข้อมูลหาย',
          'ข้อความ "ติดต่อ auth server ไม่ได้ ลองใหม่อีกสักครู่" แปลว่าเซิร์ฟเวอร์เข้าสู่ระบบไม่ตอบสนอง ให้รอสักครู่แล้วลองใหม่ ถ้ายังไม่ได้ให้แจ้งทีมไอที',
        ],
      },
      {
        id: "two-factor",
        heading: "การยืนยันตัวตนสองขั้นตอน (2FA) สำหรับผู้ดูแล",
        body: [
          'ค่าเริ่มต้นของระบบกำหนดให้ทุกคนที่มีบทบาทซึ่ง "แก้ไขข้อมูลได้" (เช่น owner, admin) ต้องเปิด 2FA ก่อนจึงจะเข้าคอนโซลได้ ส่วนบทบาทที่อ่านอย่างเดียว เช่น auditor จะบังคับหรือไม่ขึ้นกับนโยบายในหน้า "วิธีเข้าสู่ระบบ" ที่องค์กรตั้งไว้',
          'ถ้าบทบาทของคุณต้องใช้ 2FA แต่ยังไม่ได้ตั้งค่า จะเห็นแถบเตือนสีเหลือง "บทบาทของคุณต้องใช้การยืนยันตัวตนสองขั้นตอน กรุณาตั้งค่าเพื่อใช้งานต่อ" ที่ด้านบนของทุกหน้า กดลิงก์ "ตั้งค่า 2FA" เพื่อไปตั้งค่า',
        ],
        warnings: [
          'ถ้าทำโทรศัพท์ที่มีแอปยืนยันตัวตนหาย และไม่มีรหัสสำรอง ให้ติดต่อผู้ดูแลระบบคนอื่นเพื่อ "รีเซ็ต MFA" ให้ (ที่แท็บ "ความปลอดภัย" ของผู้ใช้) แล้วตั้งค่าใหม่',
        ],
      },
      {
        id: "no-access",
        heading: 'เมื่อเห็นหน้า "ไม่มีสิทธิ์เข้าถึง"',
        body: [
          'ถ้าเข้าสู่ระบบสำเร็จแต่เห็นหน้า "ไม่มีสิทธิ์เข้าถึง" พร้อมข้อความว่าบัญชีของคุณไม่มีสิทธิ์ใช้งานคอนโซล แปลว่าบัญชีนั้นยังไม่มีบทบาทคอนโซลใด ๆ (หรือมีแค่บทบาท staff ซึ่งไม่มีสิทธิ์ในคอนโซล) หรือบัญชีไม่ใช่ประเภท employee',
          'ให้ติดต่อผู้ดูแลระบบ (owner หรือ admin) เพื่อขอบทบาท แล้วกดปุ่ม "ออกจากระบบ" และเข้าสู่ระบบใหม่',
        ],
        tips: [
          "ลูกค้าหรือพาร์ทเนอร์ (ผู้ใช้ประเภทอื่นที่ไม่ใช่ employee) จะไม่ได้รับบทบาทคอนโซลเด็ดขาด เป็นการออกแบบเพื่อความปลอดภัย",
        ],
      },
      {
        id: "layout",
        heading: "ส่วนประกอบของหน้าจอ",
        body: [
          "หน้าจอคอนโซลแบ่งเป็น 3 ส่วน: แถบเมนูด้านซ้าย (Navigator) แถบหัวสีน้ำเงินด้านบน (Header) และพื้นที่เนื้อหาตรงกลาง ด้านล่างสุดของหน้ามีชื่อผลิตภัณฑ์และเลขเวอร์ชันของคอนโซล ซึ่งมีประโยชน์เวลาแจ้งปัญหา",
        ],
        fieldsTitle: "ส่วนต่าง ๆ บนหน้าจอ",
        fields: [
          {
            name: "แถบเมนูด้านซ้าย",
            description:
              'ด้านบนสุดคือชื่อผลิตภัณฑ์ ตามด้วยเมนู "ภาพรวม" และหมวดเมนูต่าง ๆ กดเมนูเพื่อไปยังหน้านั้น เมนูที่กำลังเปิดอยู่จะถูกไฮไลต์',
          },
          {
            name: 'ปุ่ม "ย่อแถบเมนู" / "ขยายแถบเมนู"',
            description:
              "ปุ่มลูกศรที่ด้านล่างของแถบเมนู (บนจอคอมพิวเตอร์) ใช้ย่อแถบเมนูให้เหลือแต่ไอคอนเพื่อให้มีพื้นที่ทำงานมากขึ้น ชี้เมาส์ที่ไอคอนเพื่อดูชื่อเมนู ระบบจำการตั้งค่านี้ไว้ในเบราว์เซอร์",
          },
          {
            name: "ปุ่มเมนู (☰) บนมือถือ",
            description:
              'บนจอเล็ก แถบเมนูด้านซ้ายจะถูกซ่อน กดปุ่ม "เปิดเมนูนำทาง" ที่มุมซ้ายบนเพื่อเปิดเมนูแบบเลื่อนออกมา',
          },
          {
            name: 'ลิงก์ "เอกสาร"',
            description:
              "อยู่แถวบนของแถบหัว เปิดเอกสาร API ของระบบในแท็บใหม่ เป็นเอกสารสำหรับนักพัฒนาที่เชื่อมระบบอื่นเข้ากับระบบนี้ ผู้ใช้คอนโซลทั่วไปไม่จำเป็นต้องใช้",
          },
          {
            name: "เมนูบัญชี (วงกลมรูปโปรไฟล์หรือตัวย่อชื่อ)",
            description:
              'อยู่มุมขวาบน กดเพื่อดูชื่อและอีเมลของคุณ เปลี่ยนภาษา เปิดหน้า "บัญชีของฉัน" หรือ "ออกจากระบบ" (ดูหัวข้อถัดไป)',
          },
          {
            name: "แถวชื่อหน้า และไอคอนช่วยเหลือ (?)",
            description:
              'แถวที่สองของแถบหัวแสดงชื่อหมวดหรือหน้าที่เปิดอยู่ ไอคอน "ช่วยเหลือ" (?) ทางขวาเปิดคู่มือการใช้งานของหน้านั้น (คู่มือที่คุณกำลังอ่านอยู่)',
          },
          {
            name: "แถวแท็บ (หัวข้อย่อย)",
            description:
              'แถวที่สามแสดงหน้าอื่น ๆ ในหมวดเดียวกัน เช่น ในหมวด "การยืนยันตัวตน" จะมีแท็บ "ผู้ใช้" "วิธีเข้าสู่ระบบ" "กลุ่ม" "บทบาท" "ฟิลด์เพิ่มเติม" แท็บที่เปิดอยู่มีเส้นขีดสีขาวด้านล่าง หน้า "ภาพรวม" ไม่มีแถวแท็บ',
          },
          {
            name: "แถบเตือนสีเหลือง",
            description:
              "แสดงเหนือเนื้อหาเมื่อมีเรื่องที่ต้องทำ เช่น ต้องตั้งค่า 2FA หรือ (สำหรับบัญชี root) ยังใช้รหัสผ่านเริ่มต้นอยู่ พร้อมลิงก์ไปทำเรื่องนั้น",
          },
        ],
      },
      {
        id: "navigator",
        heading: "เมนูในแถบด้านซ้าย",
        body: [
          "เมนูแบ่งเป็นหมวด แต่ละเมนูจะแสดงเฉพาะเมื่อคุณมีสิทธิ์อ่านข้อมูลของหน้านั้น ถ้าทั้งหมวดไม่มีเมนูที่คุณมีสิทธิ์ หมวดนั้นจะไม่แสดงเลย",
        ],
        fieldsTitle: "หมวดและเมนู",
        fields: [
          {
            name: "ภาพรวม",
            description: "ตัวเลขสรุปจำนวนผู้ใช้ การเข้าสู่ระบบ และเหตุการณ์ล่าสุด",
          },
          {
            name: "การยืนยันตัวตน",
            description:
              '"ผู้ใช้" (จัดการบัญชี) · "วิธีเข้าสู่ระบบ" (เปิด/ปิดวิธีเข้าสู่ระบบ นโยบาย 2FA และเซสชัน) · "กลุ่ม" (ทีมของผู้ใช้) · "บทบาท" (สิทธิ์ในคอนโซล) · "ฟิลด์เพิ่มเติม" (ช่องข้อมูลโปรไฟล์ที่องค์กรเพิ่มเอง)',
          },
          {
            name: "แอปพลิเคชัน",
            description: '"OAuth clients" คือรายการแอปที่ใช้ระบบนี้เข้าสู่ระบบ และกำหนดว่าใครเข้าแอปไหนได้',
          },
          {
            name: "การตรวจสอบ",
            description: '"บันทึกการใช้งาน" (audit log) บันทึกทุกการเปลี่ยนแปลงและการเข้าสู่ระบบ',
          },
          {
            name: "การตั้งค่า",
            description:
              '"ประเภทผู้ใช้" · "แบรนด์" (โลโก้ สี ข้อความหน้าเข้าสู่ระบบ) · "ข้อมูลและการส่งออก" · "ระยะเวลาเก็บบันทึก"',
          },
        ],
      },
      {
        id: "account-menu",
        heading: "เมนูบัญชี: เปลี่ยนภาษา บัญชีของฉัน และออกจากระบบ",
        shot: {
          file: "getting-started-account-menu.png",
          caption: "เมนูบัญชีที่มุมขวาบน: เลือกภาษา เปิดบัญชีของฉัน หรือออกจากระบบ",
        },
        fieldsTitle: "รายการในเมนูบัญชี",
        fields: [
          {
            name: "ชื่อและอีเมล",
            description: "บรรทัดแรกของเมนูแสดงว่าตอนนี้คุณเข้าสู่ระบบด้วยบัญชีใด",
          },
          {
            name: "ภาษา: ไทย / English",
            description:
              "เปลี่ยนภาษาของคอนโซลทันทีโดยไม่ต้องโหลดหน้าใหม่ ภาษาที่เลือกอยู่มีเครื่องหมาย ✓ นำหน้า",
          },
          {
            name: "บัญชีของฉัน",
            description:
              "เปิดหน้าบัญชีส่วนตัวบน auth server ในแท็บใหม่ ใช้เปลี่ยนรหัสผ่าน ตั้งค่า 2FA และจัดการอีเมล/เบอร์โทรของตัวเอง",
          },
          {
            name: "ออกจากระบบ",
            description:
              'ออกจากคอนโซลและกลับไปหน้าเข้าสู่ระบบ (จะเห็นข้อความ "ออกจากระบบแล้ว") ควรออกจากระบบทุกครั้งเมื่อใช้เครื่องร่วมกับผู้อื่น',
          },
        ],
      },
      {
        id: "roles",
        heading: "บทบาทและสิทธิ์ (อธิบายแบบง่าย)",
        body: [
          'สิ่งที่คุณทำได้ในคอนโซลขึ้นกับ "บทบาท" ที่ได้รับ บทบาทหนึ่งคือชุดของสิทธิ์ เช่น อ่านข้อมูลผู้ใช้ แก้ไขผู้ใช้ ลบผู้ใช้ อ่านบันทึกการใช้งาน ส่งออกข้อมูล ฯลฯ คนหนึ่งมีได้หลายบทบาท และได้สิทธิ์รวมกันของทุกบทบาท',
          'บทบาทเป็นเรื่องของ "สิทธิ์ในคอนโซล" เท่านั้น ส่วนการเข้าแอปพลิเคชันอื่น ๆ ขององค์กรกำหนดด้วย "ประเภทผู้ใช้" และ "กลุ่ม" แยกกัน',
        ],
        fieldsTitle: "บทบาทที่มีมากับระบบ",
        fields: [
          {
            name: "owner",
            description:
              "สิทธิ์ทั้งหมด รวมถึงการนำเข้าข้อมูล เฉพาะ owner เท่านั้นที่มอบหรือถอดบทบาท owner ให้คนอื่นได้ และแก้อีเมล/ช่องทางเข้าสู่ระบบของ owner คนอื่นได้",
          },
          {
            name: "admin",
            description:
              "สิทธิ์ทั้งหมดยกเว้นการนำเข้าข้อมูล ใช้กับผู้ดูแลระบบในชีวิตประจำวัน ไม่สามารถมอบบทบาท owner และระงับหรือลบบัญชีตัวเองไม่ได้",
          },
          {
            name: "auditor",
            description:
              "อ่านอย่างเดียวทุกหน้า รวมถึงบันทึกการใช้งาน เหมาะกับผู้ตรวจสอบภายใน เห็นข้อมูลแต่ไม่เห็นปุ่มแก้ไข",
          },
          {
            name: "staff",
            description: "พนักงานทั่วไป ไม่มีสิทธิ์ในคอนโซล (ผู้ที่มีแค่บทบาทนี้จะเข้าคอนโซลไม่ได้)",
          },
          {
            name: "บทบาทที่องค์กรสร้างเอง",
            description:
              'ผู้ดูแลสร้างบทบาทใหม่ที่มีสิทธิ์เฉพาะบางอย่างได้ในหน้า "บทบาท" เช่น บทบาทสำหรับทีม support ที่ดูและแก้ไขผู้ใช้ได้แต่ลบไม่ได้',
          },
        ],
        tips: [
          '"ไม่เห็นเมนู" หรือ "ไม่เห็นปุ่ม" มักหมายถึงคุณไม่มีสิทธิ์นั้น ไม่ใช่ระบบผิดพลาด เช่น ไม่มีสิทธิ์ลบผู้ใช้ก็จะไม่เห็นปุ่ม "ลบผู้ใช้" ไม่มีสิทธิ์อ่านบันทึกการใช้งานก็จะไม่เห็นเมนู "บันทึกการใช้งาน" และแท็บ "กิจกรรม" ของผู้ใช้',
          "ช่องที่เป็นสีเทากดแก้ไม่ได้ แปลว่าคุณดูได้แต่ไม่มีสิทธิ์แก้ไข",
          'ถ้าพยายามทำสิ่งที่ไม่มีสิทธิ์ จะเห็นข้อความ "คุณไม่มีสิทธิ์ดำเนินการนี้"',
          "เมื่อได้รับบทบาทใหม่ ให้ออกจากระบบแล้วเข้าใหม่ หรือโหลดหน้าเว็บใหม่ เพื่อให้เมนูอัปเดต",
        ],
      },
      {
        id: "audit",
        heading: "ทุกการเปลี่ยนแปลงถูกบันทึก",
        body: [
          'ทุกการกระทำที่เปลี่ยนแปลงข้อมูลในคอนโซล เช่น สร้างผู้ใช้ เปลี่ยนบทบาท ระงับบัญชี ตั้งรหัสผ่าน ถูกบันทึกไว้ใน "บันทึกการใช้งาน" พร้อมชื่อผู้กระทำ เวลา และ IP ผู้ที่มีสิทธิ์อ่านบันทึกตรวจสอบย้อนหลังได้ รหัสผ่านและรหัส OTP ไม่ถูกบันทึกเด็ดขาด',
        ],
      },
      {
        id: "help",
        heading: "ขอความช่วยเหลือ",
        body: [
          'กดไอคอน "ช่วยเหลือ" (?) ที่แถวชื่อหน้าเพื่อเปิดคู่มือของหน้าที่กำลังใช้งาน ในคู่มือมีรายการหัวข้อทั้งหมดให้เลือกอ่าน',
          'ถ้าเจอข้อความ "เกิดข้อผิดพลาด" ให้กดปุ่ม "ลองอีกครั้ง" หรือไอคอน "โหลดใหม่" ก่อน ถ้ายังไม่หาย ให้จดข้อความผิดพลาด เวลา และเลขเวอร์ชันที่ด้านล่างของหน้า แล้วแจ้งผู้ดูแลระบบหรือทีมไอทีขององค์กร',
          'ข้อความ "มีคำขอมากเกินไป กรุณาลองใหม่ใน … วินาที" แปลว่ากดซ้ำเร็วเกินไป ให้รอตามเวลาที่บอกแล้วลองใหม่',
        ],
      },
    ],
    related: ["overview", "users", "user-detail", "roles"],
  },
  en: {
    id: "getting-started",
    title: "Getting started with the console",
    summary:
      "The admin console is where you manage every user account, group, role, sign-in method and the applications that use the central sign-in (Single Sign-On). This page explains how to sign in, what is on the screen and what you need to know about permissions before you start.",
    access:
      "Anyone with a console role (owner, admin, auditor or a custom role) whose user type is employee can open the console. The menus and buttons you see depend on the permissions of your roles.",
    shot: {
      file: "getting-started-layout.png",
      caption:
        "The console: navigator on the left, blue header at the top and the selected page in the middle",
    },
    sections: [
      {
        id: "sign-in",
        heading: "Signing in to the console",
        body: [
          'When you open the console for the first time (or after your session expired) you see the "Sign in to the console" page. The console has no password field of its own: you sign in on the central sign-in page of the auth server — the same page employees use for the organization\'s other applications.',
          "On the central sign-in page you type any verified email, phone number, user code or username of your account, then your password (or use Google / Microsoft if your organization enabled them). Then you confirm with two-factor authentication (2FA) and come back to the console page you were on.",
          'Below the button you can switch the language ("ไทย" / "English") even before signing in.',
        ],
        shot: {
          file: "getting-started-login.png",
          caption:
            'The "Sign in to the console" page — press "Sign in with …" to continue on the central sign-in page',
        },
        steps: [
          "Open the console address you received from your administrator.",
          'Press "Sign in with Auth Platform" (the name may differ — it is your organization\'s product name). The button changes to "Redirecting to sign in…".',
          "Type your email, phone number, user code or username, then your password.",
          "Confirm the second step with a code from your authenticator app (e.g. Google Authenticator), a code by email or a backup code. If 2FA is not set up yet, you are taken to the 2FA setup first.",
          "You are brought back to the console automatically.",
        ],
        tips: [
          '"Your session expired. Sign in again to continue where you left off." means you were inactive for too long. Just sign in again — nothing is lost.',
          '"Cannot reach the auth server. Try again in a moment." means the sign-in server did not answer. Wait a moment and retry; if it persists, contact IT.',
        ],
      },
      {
        id: "two-factor",
        heading: "Two-factor authentication (2FA) for administrators",
        body: [
          'By default everyone whose role can change data (e.g. owner, admin) must have 2FA before they can enter the console. Whether read-only roles such as auditor need it depends on the policy your organization set on the "Sign-in method" page.',
          'If your role requires 2FA and you have not set it up, a yellow banner "Your role requires two-factor authentication. Set it up now to keep access." appears at the top of every page. Click "Set up 2FA" to do it.',
        ],
        warnings: [
          'If you lose the phone with your authenticator app and have no backup codes, ask another administrator to "Reset MFA" for you (user → "Security" tab), then set it up again.',
        ],
      },
      {
        id: "no-access",
        heading: 'When you see "No access"',
        body: [
          'If sign-in works but you see "No access" saying your account does not have permission to use the admin console, your account has no console role yet (or only the staff role, which has no console permissions), or it is not an employee account.',
          'Ask an administrator (owner or admin) to give you a role, then press "Sign out" and sign in again.',
        ],
        tips: [
          "Customers and partners (any user type other than employee) can never receive console roles. This is a deliberate security rule.",
        ],
      },
      {
        id: "layout",
        heading: "What is on the screen",
        body: [
          "The console has three parts: the navigator on the left, the blue header at the top and the content area in the middle. The footer shows the product name and the console version — useful when you report a problem.",
        ],
        fieldsTitle: "Parts of the screen",
        fields: [
          {
            name: "Navigator (left)",
            description:
              'The product name at the top, then "Overview" and the menu sections. Click an item to open that page; the current page is highlighted.',
          },
          {
            name: '"Collapse sidebar" / "Expand sidebar"',
            description:
              "The arrow button at the bottom of the navigator (desktop) shrinks it to icons only to give you more room. Hover an icon to see its name. The choice is remembered in your browser.",
          },
          {
            name: "Menu button (☰) on phones",
            description:
              'On small screens the navigator is hidden; press "Open navigation" at the top left to slide it in.',
          },
          {
            name: '"Docs" link',
            description:
              "In the top row of the header. Opens the API documentation in a new tab — meant for developers who connect other systems; console users normally do not need it.",
          },
          {
            name: "Account menu (your picture or initials)",
            description:
              'At the top right. Shows your name and email, the language switch, "Account" and "Sign out" (see below).',
          },
          {
            name: "Title row and help icon (?)",
            description:
              'The second header row shows the section or page you are on. The "Help" icon (?) on the right opens the manual for that page (the manual you are reading).',
          },
          {
            name: "Tabs row (sections)",
            description:
              'The third row lists the other pages of the same section; e.g. "Authentication" has "Users", "Sign-in method", "Groups", "Roles" and "Custom fields". The current tab is underlined in white. "Overview" has no tabs.',
          },
          {
            name: "Yellow banners",
            description:
              "Appear above the content when something needs your attention, e.g. you must set up 2FA, or (for the root account) the initial password is still in use — with a link to fix it.",
          },
        ],
      },
      {
        id: "navigator",
        heading: "Menus in the navigator",
        body: [
          "Menus are grouped into sections. Each item appears only when you may read that page; a section with no visible items is hidden completely.",
        ],
        fieldsTitle: "Sections and items",
        fields: [
          { name: "Overview", description: "Key numbers about users and sign-ins, and the latest events." },
          {
            name: "Authentication",
            description:
              '"Users" (accounts) · "Sign-in method" (enabled methods, 2FA policy, sessions) · "Groups" (teams of users) · "Roles" (console permissions) · "Custom fields" (extra profile fields defined by your organization)',
          },
          {
            name: "Applications",
            description:
              '"OAuth clients" — the applications that use this sign-in, and who may sign in to each.',
          },
          {
            name: "Monitor",
            description: '"Audit logs" — a record of every change and sign-in.',
          },
          {
            name: "Settings",
            description:
              '"User types" · "Branding" (logo, colors, sign-in page text) · "Data & export" · "Log retention"',
          },
        ],
      },
      {
        id: "account-menu",
        heading: "Account menu: language, Account and Sign out",
        shot: {
          file: "getting-started-account-menu.png",
          caption: "The account menu at the top right: language, Account and Sign out",
        },
        fieldsTitle: "Items in the account menu",
        fields: [
          {
            name: "Name and email",
            description: "The first line shows which account you are signed in with.",
          },
          {
            name: "Language: ไทย / English",
            description:
              "Switches the console language immediately, without reloading. The current language has a ✓ in front.",
          },
          {
            name: "Account",
            description:
              "Opens your personal account page on the auth server in a new tab: change your password, set up 2FA, manage your own emails and phone numbers.",
          },
          {
            name: "Sign out",
            description:
              'Leaves the console and returns to the sign-in page ("You have signed out."). Always sign out on a shared computer.',
          },
        ],
      },
      {
        id: "roles",
        heading: "Roles and permissions in plain words",
        body: [
          "What you can do in the console depends on your roles. A role is a set of permissions such as read users, change users, delete users, read audit logs, export data, and so on. A person can have several roles and gets the permissions of all of them combined.",
          "Roles are only about the console. Access to the organization's other applications is decided separately by the user type and groups.",
        ],
        fieldsTitle: "Built-in roles",
        fields: [
          {
            name: "owner",
            description:
              "Everything, including data import. Only owners can give or take away the owner role, and change another owner's email or sign-in identifiers.",
          },
          {
            name: "admin",
            description:
              "Everything except data import — for day-to-day administrators. Cannot grant owner, and cannot ban or delete their own account.",
          },
          {
            name: "auditor",
            description:
              "Read-only on every page, including audit logs — for internal auditors. Sees the data but no edit buttons.",
          },
          {
            name: "staff",
            description:
              "Regular employee with no console permissions (someone with only this role cannot open the console).",
          },
          {
            name: "Custom roles",
            description:
              'Administrators can create roles with selected permissions on the "Roles" page, e.g. a support role that can view and edit users but not delete them.',
          },
        ],
        tips: [
          '"I don\'t see a menu" or "I don\'t see a button" usually means you lack that permission — it is not an error. Without the delete permission there is no "Delete user" button; without audit log access there is no "Audit logs" menu and no "Activity" tab on users.',
          "Greyed-out fields mean you can look but not change.",
          'If you try something you may not do, you see "You do not have permission to perform this action."',
          "After you receive a new role, sign out and in again (or reload the page) so the menus update.",
        ],
      },
      {
        id: "audit",
        heading: "Every change is recorded",
        body: [
          'Every change made in the console — creating users, changing roles, banning, setting passwords — is written to the "Audit logs" with who did it, when and from which IP. People with audit log access can review it later. Passwords and one-time codes are never recorded.',
        ],
      },
      {
        id: "help",
        heading: "Getting help",
        body: [
          'Click the "Help" icon (?) in the title row to open the manual for the page you are on. The manual lists all topics.',
          'If you see "Something went wrong", press "Retry" or the "Reload" icon first. If it keeps happening, note the message, the time and the version number at the bottom of the page and contact your administrator or IT team.',
          '"Too many requests. Try again in … seconds." means you clicked too quickly; wait the time shown and try again.',
        ],
      },
    ],
    related: ["overview", "users", "user-detail", "roles"],
  },
};

export default topic;
